import { useMemo } from 'react';

import { useBreakpointFlags } from 'hooks/useBreakpointFlags';

const SMALL_WORDS_COUNT = 20;
const MEDIUM_WORDS_COUNT = 40;

/**
 * Responsive values object for different breakpoints
 *
 * @typedef {Object} ResponsiveValues
 * @property {number} xs - value for extra small screens (mobile)
 * @property {number} sm - value for small screens (mobile landscape)
 * @property {number} md - value for medium screens (tablets)
 * @property {number} lg - value for large screens (desktops)
 * @property {number} xl - value for extra large screens (large desktops)
 * @property {number} xxl - value for extra extra large screens (very large desktops)
 */

/**
 * Configuration object for responsive wordcloud
 *
 * @typedef {Object} WordCloudResponsiveConfig
 * @property {number} gridSizeFactor - Factor that affects the grid size for word placement
 * @property {number[]} fontSizes - Array of [min, max] font sizes for the wordcloud
 * @property {number} containerHeight - Height of the wordcloud container
 * @property {number} sameWeightValue - Value used for words with the same weight
 */

/**
 * Returns responsive container height values based on the number of words
 *
 * @param {number} wordsCount - Number of words in the cloud
 * @returns {ResponsiveValues} Responsive height values for different breakpoints
 */
const getResponsiveContainerHeightByWordsCount = (wordsCount = 0) => {
  if (wordsCount <= SMALL_WORDS_COUNT) {
    return {
      xs: 250,
      sm: 250,
      md: 300,
      lg: 300,
      xl: 350,
      xxl: 400,
    };
  }

  if (wordsCount <= MEDIUM_WORDS_COUNT) {
    return {
      xs: 400,
      sm: 300,
      md: 350,
      lg: 450,
      xl: 500,
      xxl: 550,
    };
  }

  return {
    xs: 550,
    sm: 400,
    md: 450,
    lg: 500,
    xl: 600,
    xxl: 700,
  };
};

/**
 * Returns responsive sameWeightValue values based on the number of words
 *
 * @param {number} wordsCount - Number of words in the cloud
 * @returns {ResponsiveValues} Responsive sameWeightValue values for different breakpoints
 */
const getResponsiveSameWeightValueByWordsCount = (wordsCount = 0) => {
  if (wordsCount <= SMALL_WORDS_COUNT) {
    return {
      xs: 14,
      sm: 22,
      md: 32,
      lg: 32,
      xl: 35,
      xxl: 40,
    };
  }

  if (wordsCount <= MEDIUM_WORDS_COUNT) {
    return {
      xs: 11,
      sm: 14,
      md: 22,
      lg: 28,
      xl: 29,
      xxl: 32,
    };
  }

  return {
    xs: 11,
    sm: 16,
    md: 20,
    lg: 23,
    xl: 25,
    xxl: 28,
  };
};

/**
 * Hook to generate responsive configuration for the wordcloud
 *
 * @param {number} wordsCount - Number of words in the cloud
 * @returns {WordCloudResponsiveConfig} Responsive configuration for the wordcloud
 */
export const useResponsiveConfig = (wordsCount) => {
  const { getResponsiveValueForCurrent } = useBreakpointFlags();

  return useMemo(
    () => ({
      gridSizeFactor: getResponsiveValueForCurrent({
        xs: 35,
        sm: 40,
        md: 50,
        lg: 50,
        xl: 45,
        xxl: 45,
      }),
      fontSizes: getResponsiveValueForCurrent({
        xs: [10, 40],
        sm: [14, 50],
        md: [14, 70],
        lg: [14, 90],
        xl: [16, 90],
        xxl: [18, 105],
      }),
      containerHeight: getResponsiveValueForCurrent(getResponsiveContainerHeightByWordsCount(wordsCount)),
      sameWeightValue: getResponsiveValueForCurrent(getResponsiveSameWeightValueByWordsCount(wordsCount)),
    }),
    [getResponsiveValueForCurrent, wordsCount],
  );
};
